/**
 * ComparisonTableWidget — per-site comparison table (sites × metrics). Powers
 * the Corporate site-comparison view (TRIR/LTIFR/… by site). Cross-site by
 * nature, so it ignores the single selected site and lists all accessible
 * sites; time + dimension filters still apply.
 */
import React from 'react';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import { useDashboard } from '../context/DashboardContext';
import { useDashboardData } from '../hooks/useDashboardData';
import { fetchComparison } from '../api/client';
import { formatMetricValue } from '../utils/format';

export default function ComparisonTableWidget({ title, metrics = [] }) {
  const { module, time, dimensions, standard, refreshMs } = useDashboard();
  const timeKey = time ? JSON.stringify(time) : '';
  const dimsKey = dimensions ? JSON.stringify(dimensions) : '';

  const { data, loading, error } = useDashboardData(
    (signal) => {
      const body = { module, metrics };
      if (time) body.time = time;
      if (dimensions && Object.keys(dimensions).length) body.dimensions = dimensions;
      if (standard) body.standard = standard;
      return fetchComparison(body, { signal });
    },
    [module, metrics.join(','), timeKey, dimsKey, standard],
    { enabled: !!module && metrics.length > 0, refreshMs },
  );

  const cols = data?.metrics || [];
  const sites = data?.sites || [];

  return (
    <div className="rounded-xl border border-gray-200/80 bg-white p-4 shadow-sm transition-shadow duration-200 hover:shadow-md">
      {title && <h3 className="mb-3 text-sm font-semibold text-gray-800">{title}</h3>}
      {loading && <div className="h-40 animate-pulse rounded bg-gray-100" />}
      {!loading && error && <div className="py-6 text-center text-sm text-red-600">Failed to load comparison</div>}
      {!loading && !error && (
        <div className="overflow-x-auto rounded-md border border-gray-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Site</TableHead>
                {cols.map((c) => <TableHead key={c.key} className="text-right">{c.label}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sites.length === 0 && (
                <TableRow><TableCell colSpan={cols.length + 1} className="py-6 text-center text-gray-400">No sites</TableCell></TableRow>
              )}
              {sites.map((s) => (
                <TableRow key={s.site_id}>
                  <TableCell className="font-medium">{s.site_name}</TableCell>
                  {cols.map((c) => (
                    <TableCell key={c.key} className="text-right tabular-nums">
                      {formatMetricValue(s.values?.[c.key], c.format)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
